import React, { useState } from 'react';
import { Box } from '@mui/material';
import { DESTINATIONS_WAREHOUSE_DOC } from '../../../../../core/constants/constants';
import { DestinationMode } from '../../../../../core/interfaces/destination-mode';
import { HevoEntity, HevoEntityItem } from '../../../../../core/interfaces/hevo-entity';
import { getDestinationModeKeywords } from '../../../../../connector/connector-form/utils/utils';
import { StandardPipeline } from '../../../../../edge/pipelines/interfaces/integration';
import { UpgradePipelineBanner } from '../../../../../edge/pipelines/UpgradePipeline/UpgradePipelineBanner';
import { WizardStepBaseProps } from '../../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../../wizard/hooks/useWizardStepData';
import { DestinationVersions } from '../../../interfaces/destination';
import { SelectionDestinationTypeHeaderType } from '../../interfaces/selectDestinationType';
import DestinationTypeList from '../select-destination-type-list/DestinationTypeList';
import SelectDestinationTypeHeader from '../select-destination-type-header/SelectDestinationTypeHeader';
import { HdWizardStepController } from '../../../../../wizard/wizard-native/hd-wizard-step-controller';
import { getDataIdGenerator } from '../../../../../core/utils/generateDataId';

interface SelectDestinationTypeProps extends Omit<WizardStepBaseProps, 'controller'> {
  controller?: Pick<HdWizardStepController, 'params' | 'data'>;
}

export const dataIdGenerator = getDataIdGenerator('select-destination-type');

export function SelectDestinationType({
  initialData,
  controller,
  completeStep,
  onDataChange,
  markDirty
}: SelectDestinationTypeProps) {
  const [data, setData] = useWizardStepData(initialData, onDataChange);

  const destinationMode = controller.data.destinationMode || DestinationMode.DESTINATION;
  const destinationKeyword = getDestinationModeKeywords(destinationMode).singular;
  const hevoEntityType = controller.params.hevoEntityType || HevoEntity.DESTINATION;
  const sourceType = controller.data.sourceType;

  const onTypeSelect = (destType: any) => {
    if (!data.destinationType || data.destinationType.dest_type !== destType.dest_type) {
      markDirty();
    }

    setData((d: object) => ({
      ...d,
      destinationType: destType,
      setupWarehouse: false
    }));

    completeStep();
  };

  const setupWarehouse = () => {
    setData((d: object) => ({
      ...d,
      destinationType: null,
      setupWarehouse: true
    }));

    completeStep();
  };

  return (
    <SelectDestinationTypeBase
      versions={[DestinationVersions.STANDARD]}
      managedWarehouse={controller.params.managedWarehouse}
      sourceType={sourceType}
      destinationKeyword={destinationKeyword}
      headerType={controller.params.headerType}
      filterMethod={controller.data?.filterMethod}
      destinationMode={destinationMode}
      setupWarehouse={setupWarehouse}
      hevoEntityType={hevoEntityType}
      search={data._search}
      onSearch={(term: string) => {
        setData((d: object) => ({
          ...d,
          _search: term
        }));
      }}
      onTypeSelect={onTypeSelect}
    />
  );
}

export function SelectDestinationTypeBase({
  managedWarehouse,
  sourceType,
  headerType,
  destinationKeyword,
  filterMethod,
  destinationMode,
  hevoEntityType,
  setupWarehouse,
  search,
  onSearch,
  onTypeSelect,
  disableSelection,
  versions,
  commonSelection,
  isUpgradingPipeline,
  standardPipelineInfo,
  scrollElement
}: {
  managedWarehouse?: boolean;
  sourceType?: any;
  destinationKeyword: string;
  headerType?: SelectionDestinationTypeHeaderType;
  filterMethod?: Function;
  destinationMode: DestinationMode;
  hevoEntityType: HevoEntityItem;
  setupWarehouse?: Function;
  search: string;
  onSearch: (term: string) => void;
  onTypeSelect: Function;
  disableSelection?: (destinationType: any) => boolean;
  versions: DestinationVersions[];
  commonSelection?: boolean;
  isUpgradingPipeline?: boolean;
  standardPipelineInfo?: StandardPipeline;
  scrollElement?: HTMLElement | null;
}) {
  const [scrollElementState, setScrollElementState] = useState<HTMLDivElement | null>(null);

  return (
    <div
      className='scrollable-content bg-surface'
      id='scrollable-view'
      ref={setScrollElementState}
      data-id={dataIdGenerator('inner')}
    >
      <div className='body-container'>
        <Box>
          {isUpgradingPipeline ? (
            <div style={{ maxWidth: '820px', margin: 'auto' }}>
              <UpgradePipelineBanner
                standardPipelineInfo={standardPipelineInfo}
                isObjectConfigPage={false}
              />
            </div>
          ) : null}

          <SelectDestinationTypeHeader
            docLink={!versions.includes(DestinationVersions.EDGE) ? DESTINATIONS_WAREHOUSE_DOC : undefined}
            destinationKeyword={destinationKeyword}
            headerType={headerType || SelectionDestinationTypeHeaderType.SELECT_DESTINATION_TYPE}
          />

          <DestinationTypeList
            destinationKeyword={destinationKeyword}
            destinationMode={destinationMode}
            hevoEntityType={hevoEntityType}
            onTypeSelect={onTypeSelect}
            saveSearch={onSearch}
            defaultSearch={search}
            filterMethod={filterMethod}
            managedWarehouse={{
              canResolveManagedWarehouse: !!(managedWarehouse && sourceType),
              sourceType: sourceType?.source_type,
              setupWarehouse: setupWarehouse
            }}
            showManageWarehouseCard={false}
            disableSelection={disableSelection}
            versions={versions}
            commonSelection={commonSelection}
            scrollElement={scrollElement ?? scrollElementState}
          />
        </Box>
      </div>
    </div>
  );
}
